<script setup lang="ts">
    import PkAvatar from './PkAvatar.vue'
    import PkStreamingMarkdown from './PkStreamingMarkdown.vue'

    defineProps<{
        greeting?: string
        /** Agent avatar shown above the greeting where the input chip is hidden (mobile) */
        logo?: string | null
        name?: string
    }>()
</script>

<template>
    <div class="pk-chatbot-empty-state">
        <PkAvatar
            v-if="logo || name"
            class="pk-chatbot-empty-state__avatar"
            modifiers="surface"
            :img-src="logo"
            :name="name" />
        <PkStreamingMarkdown
            v-if="greeting"
            :markdown="greeting"
            :animation="null"
            class="wysiwyg pk-chatbot-empty-state__greeting" />
    </div>
</template>

<style lang="scss">
    .pk-chatbot-empty-state {
        // Above the fullscreen ambient glow
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--spacing-sm);
        padding: var(--spacing-md);
        text-align: center;

        // Let the greeting wrap on a couple of lines (Gemini-like). Keyed on
        // the chat's own width (`--phone`, measured in PkChatbot): a narrow
        // preview frame on a desktop screen wraps like a phone
        .pk-chatbot--phone & {
            padding-inline: var(--spacing-56);
        }

        &__avatar {
            width: var(--spacing-64);
            height: var(--spacing-64);
            font-size: var(--text-20);
            border-radius: var(--rounded-full);

            // The agent identity lives in the input chip when that is
            // visible: the chip hides itself at `--tight`, so keying the
            // avatar on the absence of that modifier shows exactly one of
            // the two
            .pk-chatbot:not(.pk-chatbot--tight) & {
                display: none;
            }
        }

        &__greeting {
            font-size: var(--text-28);
            font-weight: var(--font-light);
            color: var(--color-word-1);
            text-wrap: balance;

            // Render the greeting markdown inline as a single heading line
            :first-child {
                margin-top: 0;
            }

            :last-child {
                margin-bottom: 0;
            }
        }

        // Enters fading in from below, in sync with the glow ignition
        &__avatar,
        &__greeting {
            animation: pk-chatbot-greeting-enter 600ms var(--ease-out) both;

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
        }
    }

    @keyframes pk-chatbot-greeting-enter {
        from {
            opacity: 0;
            transform: translateY(var(--spacing-16));
        }
    }
</style>
